import { useState, useEffect } from 'react'
import { profileApi, type BrokerProfile } from '../api/profileApi'
import { useAuth } from '../contexts/AuthContext'

export default function BrokerProfileForm() {
  const { getAccessToken } = useAuth()
  const [profile, setProfile] = useState<BrokerProfile>({})
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')
  const [success, setSuccess] = useState(false)

  useEffect(() => {
    const load = async () => {
      const token = getAccessToken()
      if (!token) {
        setLoading(false)
        return
      }
      try {
        const { profile: p } = await profileApi.getBrokerProfile(token)
        setProfile(p || {})
      } catch {
        setError('Could not load profile')
      } finally {
        setLoading(false)
      }
    }
    load()
  }, [getAccessToken])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    const token = getAccessToken()
    if (!token) {
      setError('Please sign in to save your profile')
      return
    }
    setSaving(true)
    setError('')
    setSuccess(false)
    try {
      await profileApi.putBrokerProfile(token, profile)
      setSuccess(true)
      setTimeout(() => setSuccess(false), 3000)
    } catch {
      setError('Failed to save profile')
    } finally {
      setSaving(false)
    }
  }

  if (loading) {
    return (
      <div className="card animate-pulse">
        <div className="h-6 bg-gray-200 rounded w-1/3 mb-4" />
        <div className="space-y-4">
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="h-10 bg-gray-200 rounded" />
          ))}
        </div>
      </div>
    )
  }

  return (
    <div className="card">
      <h2 className="text-xl font-bold text-gray-900 mb-6">Broker Profile</h2>

      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="grid md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">First name</label>
            <input
              type="text"
              value={profile.first_name ?? ''}
              onChange={(e) => setProfile((p) => ({ ...p, first_name: e.target.value }))}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              placeholder="Jane"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Last name</label>
            <input
              type="text"
              value={profile.last_name ?? ''}
              onChange={(e) => setProfile((p) => ({ ...p, last_name: e.target.value }))}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              placeholder="Williams"
            />
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Business name</label>
          <input
            type="text"
            value={profile.business_name ?? ''}
            onChange={(e) => setProfile((p) => ({ ...p, business_name: e.target.value }))}
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            placeholder="Williams Mortgage Solutions"
          />
        </div>

        <div className="grid md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">License number</label>
            <input
              type="text"
              value={profile.license_number ?? ''}
              onChange={(e) => setProfile((p) => ({ ...p, license_number: e.target.value }))}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              placeholder="MFAA/CCA number"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Years experience</label>
            <input
              type="number"
              value={profile.years_experience ?? ''}
              onChange={(e) =>
                setProfile((p) => ({
                  ...p,
                  years_experience: e.target.value ? parseInt(e.target.value, 10) : undefined,
                }))
              }
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              placeholder="5"
              min={0}
              max={50}
            />
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Specialisation</label>
          <input
            type="text"
            value={profile.specialization ?? ''}
            onChange={(e) => setProfile((p) => ({ ...p, specialization: e.target.value }))}
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            placeholder="First home buyers, investment, refinancing"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Bio</label>
          <textarea
            value={profile.bio ?? ''}
            onChange={(e) => setProfile((p) => ({ ...p, bio: e.target.value }))}
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            rows={4}
            placeholder="Tell customers about your experience and approach..."
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Photo URL</label>
          <input
            type="url"
            value={profile.photo_url ?? ''}
            onChange={(e) => setProfile((p) => ({ ...p, photo_url: e.target.value }))}
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            placeholder="https://..."
          />
        </div>

        <div className="flex items-center gap-3">
          <input
            type="checkbox"
            id="is_accepting_clients"
            checked={profile.is_accepting_clients ?? true}
            onChange={(e) =>
              setProfile((p) => ({ ...p, is_accepting_clients: e.target.checked }))
            }
            className="h-4 w-4 text-primary-600 rounded focus:ring-primary-500"
          />
          <label htmlFor="is_accepting_clients" className="text-sm font-medium text-gray-700">
            Accepting new clients
          </label>
        </div>

        {error && (
          <div className="p-4 bg-red-50 border border-red-200 rounded-lg text-red-600 text-sm">
            {error}
          </div>
        )}

        {success && (
          <div className="p-4 bg-green-50 border border-green-200 rounded-lg text-green-700 text-sm">
            Profile saved successfully
          </div>
        )}

        <button
          type="submit"
          disabled={saving}
          className="btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {saving ? 'Saving...' : 'Save Profile'}
        </button>
      </form>
    </div>
  )
}
